import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import DataLoadError from '@/components/errors/DataLoadError';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import CircularChartCard from '@/app/Serp/components/CircularChartCard';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { KpiStatCard } from '../components/common/KpiStatCard';
import { SourcesPageSkeleton } from '../components/common/Skeleton';
import { NoProject } from '../components/NoProject';
import { PageFilterBar } from '../components/filters/PageFilterBar';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { SourceTrendChart } from '../components/sources/SourceTrendChart';
import { sourceDomainsColumns } from '../config/sourceDomainsColumns';
import { useProject } from '../context/ProjectContext';
import {
  formatSourceCount,
  formatSourceDisplayType,
  formatSourcePercent
} from '../utils/source-insights';
import { getHelpHtml } from '../utils/help/help-content';
import { buildDateRangeQueryOptions } from '../utils/visibility-range';
import type {
  SourceInsightsDomainsResult,
  SourceInsightsSummary,
  SourceInsightsTrendResult,
  SourceInsightsTypesResult
} from '../types';
const CIRCULAR_CHART_COLORS = ['primary', 'info', 'success', 'warning', 'danger'] as const;

function circularChartColor(index: number): string {
  return CIRCULAR_CHART_COLORS[index % CIRCULAR_CHART_COLORS.length];
}

function CardHeaderTitle({
  title,
  subtitle,
  helpKey
}: {
  title: string;
  subtitle?: string;
  helpKey: string;
}) {
  const helpHtml = getHelpHtml(helpKey);
  return (
    <div>
      <span className="d-inline-flex align-items-center gap-1">
        {title}
        {helpHtml ? <HelpTooltip html={helpHtml} /> : null}
      </span>
      {subtitle ? (
        <p className="fs--2 text-700 mb-0 fw-normal mt-2">{subtitle}</p>
      ) : null}
    </div>
  );
}

type AgGridWrapperProps = {
  table_name?: string;
  height?: string;
  columns: unknown[];
  data: unknown[];
  settings?: Record<string, unknown>;
  otherOptions?: Record<string, unknown>;
};
const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

function SourceFilterEmptyState({ onClear }: { onClear: () => void }) {
  return (
    <Card className="border-dashed shadow-none">
      <Card.Body className="py-5 text-center d-flex flex-column align-items-center">
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-body-tertiary text-primary mb-3"
          style={{ width: '2.75rem', height: '2.75rem' }}
          aria-hidden
        >
          <FontAwesomeIcon icon="filter" />
        </span>
        <p className="fs-0 fw-semibold text-800 mb-2">منبعی پیدا نشد</p>
        <p className="fs--1 text-500 mb-3" style={{ maxWidth: '28rem' }}>
          هیچ منبعی با فیلترهای انتخاب‌شده مطابقت ندارد. فیلترها را تغییر دهید یا پاک کنید.
        </p>
        <Button type="button" size="sm" variant="falcon-default" onClick={onClear}>
          پاک کردن فیلترها
        </Button>
      </Card.Body>
    </Card>
  );
}

export function SourcesPage() {
  return (
    <NoProject>
      <SourcesContent />
    </NoProject>
  );
}

function SourcesContent() {
  const {
    projectId,
    topicFilterId,
    setTopicFilterId,
    modelFilterId,
    setModelFilterId,
    setDomainFilterId,
    visibilityRange,
    customFrom,
    customTo,
    sourceFilterIds,
    setSourceFilterIds
  } = useProject();

  const rangeQuery = useMemo(
    () =>
      buildDateRangeQueryOptions(visibilityRange, customFrom, customTo, {
        topicId: topicFilterId ?? undefined,
        providerIds: modelFilterId ? [modelFilterId] : undefined,
        sourceIds: sourceFilterIds.length > 0 ? sourceFilterIds : undefined
      }),
    [
      visibilityRange,
      customFrom,
      customTo,
      topicFilterId,
      modelFilterId,
      sourceFilterIds
    ]
  );

  const [summary, setSummary] = useState<SourceInsightsSummary | null>(null);
  const [domains, setDomains] = useState<SourceInsightsDomainsResult | null>(null);
  const [types, setTypes] = useState<SourceInsightsTypesResult | null>(null);
  const [trend, setTrend] = useState<SourceInsightsTrendResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const [summaryData, domainsData, typesData, trendData] = await Promise.all([
          api.getSourceInsightsSummary(projectId!, rangeQuery),
          api.getSourceInsightsDomains(projectId!, { ...rangeQuery, page: 1, perPage: 100 }),
          api.getSourceInsightsTypes(projectId!, rangeQuery),
          api.getSourceInsightsTrend(projectId!, { ...rangeQuery, topN: 5 })
        ]);
        if (!cancelled) {
          setSummary(summaryData);
          setDomains(domainsData);
          setTypes(typesData);
          setTrend(trendData);
          setError(false);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setSummary(null);
          setDomains(null);
          setTypes(null);
          setTrend(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [projectId, rangeQuery, retryNonce]);

  const hasAnySources = (summary?.totalSources ?? 0) > 0;
  const hasFilteredData = hasAnySources;
  const emptyAfterFilters =
    !loading &&
    !error &&
    summary != null &&
    summary.totalSources === 0 &&
    (sourceFilterIds.length > 0 ||
      topicFilterId != null ||
      modelFilterId != null ||
      visibilityRange !== 'all');

  const sourceTypeChartData = useMemo(
    () =>
      (types?.items ?? []).map((item, index) => ({
        name: formatSourceDisplayType(item.type),
        count: item.citations,
        percentage: item.percent,
        color: circularChartColor(index)
      })),
    [types]
  );

  return (
    <div>
      <div className="mb-4">
        <PageFilterBar showSources splitDateAndFilters />
      </div>

      {error ? (
        <DataLoadError
          onRetry={() => setRetryNonce(n => n + 1)}
          isRetrying={loading}
        />
      ) : loading ? (
        <SourcesPageSkeleton />
      ) : null}

      {!loading && !error && !hasAnySources && !emptyAfterFilters && (
        <Card className="border-dashed">
          <Card.Body className="py-5 text-center">
            <p className="fs-0 fw-semibold text-800 mb-2">
              هنوز منبعی از پاسخ‌های AI استخراج نشده است.
            </p>
            <p className="fs--1 text-500 mb-0">
              برای نمایش منابع، پرامپت‌ها باید اجرا شوند و پاسخ‌ها شامل URL یا citation باشند.
            </p>
          </Card.Body>
        </Card>
      )}

      {emptyAfterFilters && (
        <SourceFilterEmptyState
          onClear={() => {
            setTopicFilterId(null);
            setModelFilterId(null);
            setDomainFilterId('all');
            setSourceFilterIds([]);
          }}
        />
      )}

      {!loading && !error && hasFilteredData && (
        <>
          <Row className="g-3 mb-4">
            <Col xs={6} xl={3}>
              <KpiStatCard
                imageIndex={0}
                label={
                  <span className="d-inline-flex align-items-center gap-1">
                    منابع شناسایی‌شده
                    <HelpTooltip html={getHelpHtml('identified_sources')} />
                  </span>
                }
                value={formatSourceCount(summary?.totalSources)}
                valueClassName="text-primary"
              />
            </Col>
            <Col xs={6} xl={3}>
              <KpiStatCard
                imageIndex={1}
                label={
                  <span className="d-inline-flex align-items-center gap-1">
                    کل Citationها
                    <HelpTooltip html={getHelpHtml('total_citations')} />
                  </span>
                }
                value={formatSourceCount(summary?.totalCitations)}
                valueClassName="text-warning"
              />
            </Col>
            <Col xs={6} xl={3}>
              <KpiStatCard
                imageIndex={2}
                label={
                  <span className="d-inline-flex align-items-center gap-1">
                    پوشش منبع
                    <HelpTooltip html={getHelpHtml('source_coverage')} />
                  </span>
                }
                value={formatSourcePercent(summary?.sourceCoverage)}
                valueClassName="text-info"
                subtext="درصد پاسخ‌هایی که حداقل یک منبع دارند"
              />
            </Col>
            <Col xs={6} xl={3}>
              <KpiStatCard
                imageIndex={3}
                label={
                  <span className="d-inline-flex align-items-center gap-1">
                    نوع منبع غالب
                    <HelpTooltip html={getHelpHtml('top_source_type')} />
                  </span>
                }
                value={formatSourceDisplayType(summary?.topDisplayType)}
                valueClassName="text-success"
              />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xl={6}>
              <Card className="h-100">
                <FalconCardHeader
                  light
                  titleTag="h6"
                  className="py-3"
                  title={
                    <CardHeaderTitle
                      title="روند استفاده از منابع"
                      subtitle="درصد پاسخ‌هایی که هر منبع در آن ظاهر شده"
                      helpKey="source_usage_trend"
                    />
                  }
                />
                <Card.Body>
                  <SourceTrendChart trend={trend} />
                </Card.Body>
              </Card>
            </Col>
            <Col xl={6}>
              {sourceTypeChartData.length > 0 ? (
                <CircularChartCard
                  className="h-100"
                  title={
                    <CardHeaderTitle
                      title="نوع منابع"
                      subtitle="ترکیب Citationها بر اساس نوع منبع"
                      helpKey="source_types"
                    />
                  }
                  data={sourceTypeChartData}
                />
              ) : (
                <Card className="h-100">
                  <FalconCardHeader
                    light
                    titleTag="h6"
                    className="py-3"
                    title={
                      <CardHeaderTitle
                        title="نوع منابع"
                        subtitle="ترکیب Citationها بر اساس نوع منبع"
                        helpKey="source_types"
                      />
                    }
                  />
                  <Card.Body className="d-flex align-items-center justify-content-center fs--1 text-500">
                    داده‌ای برای نمودار نوع منابع نیست
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>

          <Card className="overflow-hidden ag-grid-in-card">
            <FalconCardHeader
              light
              titleTag="h6"
              className="py-3"
              title={
                <span className="d-inline-flex align-items-center gap-1">
                  منابع شناسایی‌شده
                  <HelpTooltip html={getHelpHtml('identified_sources_table')} />
                </span>
              }
            />
            <Card.Body>
              <TypedAgGridWrapper
                table_name="tracker_ai_sources"
                columns={sourceDomainsColumns}
                data={domains?.rows ?? []}
                height="520px"
                otherOptions={{
                  rowHeight: 48
                }}
                settings={{
                  pagination: true,
                  fullscreen: false,
                  changeHeight: false,
                  saveGrid: false,
                  resetGrid: false,
                  sideBar: false
                }}
              />
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
}

export default SourcesPage;
